import styled from "styled-components";
import BaseButton from "components/BaseButton";
import {pillShape} from "components/BaseButton.styles";
import {radii, showcase} from "../../theme/tokens";
import {Counter} from "../../account/brand/PageThemeSection.styles";
import {TileRemoveButton} from "../../account/brand/TileRemoveButton";
import {FooterActions, SectionLabel} from "./OptionsSheet.styles";

/**
 * Externalized styles for {@link ./PersonaliseSheet} (ui#556): only what the per-transfer composer
 * adds on top of what it reuses. The section grammar, the compose fields and the sticky footer come
 * from `OptionsSheet.styles`; the image tiles come from the account personalization sheet; the
 * animation tiles are {@link ./TemplateTilePicker.styles}. The logo tile is this sheet's own since
 * ui#1942 made the picture the picker, and the footer's pair sits on the shared `BaseButton` since
 * kitecorp/transferify-ui#1953. Everything here is token-driven (epic #26), so light and dark fall out
 * of the token layer.
 */

/**
 * The signed-out pitch's plan line: every plan includes the look, said to a visitor who has no offer of their
 * own (ui#764, kitecorp/transferify#525). For a signed-in sender it is a chip on the look's label instead
 * ({@link EntitlementChip}, kitecorp/transferify-ui#1953).
 *
 * Reads as a plain sentence rather than as a tag, deliberately: a visitor arrives here to look around.
 */
export const GuestPlanNote = styled.p`
	margin: 8px 0 0;
	font-size: 13px;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink};
`;

/** The quieter second line: an invitation to look around and log in. */
export const GuestHint = styled.p`
	margin: 4px 0 0;
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The "From your account / Changed for this transfer / New for this transfer" tag beside a field label.
 *
 * A per-transfer personalization is a SNAPSHOT layered over the account's own, and a composer that did
 * not say which is which would leave the sender guessing whether an untouched field means "keep mine"
 * or "send nothing". Rendered as a quiet outline rather than a coloured chip so three of them down the
 * sheet read as annotation, not as three warnings.
 */
export const OriginBadge = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 2px 7px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: ${radii.pill};
	font-size: 10.5px;
	font-weight: 700;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink2};
	white-space: nowrap;
`;

/**
 * The look's chip ("Free"), on its own line above the first branded control (owner 2026-09-12). A row
 * rather than a bare chip so the spacing matches the section labels it sits above.
 */
export const LookChipRow = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 12px;
`;

/**
 * A section's label row: the uppercase label on the reading edge, its chips on the far edge
 * (kitecorp/transferify-ui#1953, owner-approved prototype). The chips are the origin badge, the
 * entitlement chip and the images counter, whichever of them the section has.
 *
 * The ROW owns the leading under it. `SectionLabel` carries its own 12px margin for the sections that
 * mount it alone, and inside a centred flex row that margin is part of the label's box, so the chips
 * beside it sat 6px below its centre line. Zeroed here and re-applied on the row, so the label and its
 * chips share one centre and the control below still gets its gap.
 */
export const LabelRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 10px;

	> ${SectionLabel} {
		margin: 0 auto 0 0;
	}

	/* The account editor's counter pins itself to the top of its flex row; in this row it is a chip
	   beside a label and sits on the label's centre like the other two. */
	> ${Counter} {
		align-self: center;
	}
`;

/**
 * A section's entitlement chip: it reads "Free" on the sender's own words and on the look alike
 * (kitecorp/transferify-ui#1953, kitecorp/transferify#525). It replaced the standalone "Included with your
 * plan." lines above every section, so the ladder reads where each control is.
 *
 * The builder's paywall is gone (ui#1814), nothing here is dimmed and nothing is inert, so these chips say
 * that every half is free, and they take the signal tint with its guaranteed-readable ink so they read as
 * good news rather than as a lock.
 */
export const EntitlementChip = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 2px 8px;
	border-radius: ${radii.pill};
	background: ${({theme}) => theme.tokens.signalTint};
	color: ${({theme}) => theme.tokens.signalInk};
	font-size: 10.5px;
	font-weight: 700;
	letter-spacing: 0.03em;
	white-space: nowrap;
`;

/**
 * The footer's pair, and the locked rung's way in, on the shared primitive (kitecorp/transferify-ui#1953,
 * owner-approved prototype): pills, in `BaseButton`'s own roles.
 *
 * `styled(BaseButton)` rather than the options sheet's `FooterAction`, which is that primitive's own
 * adoption rule: a surface converts when it is next substantially reworked, and this footer is being
 * reworked. It brings the shared 44px control height, MUI's button semantics, one `Mui-disabled` hook
 * and the focus ring; nothing below repaints any of that. The corner comes from the primitive's shared
 * {@link pillShape}, so it is the same corner the progress card's Personalise button and the miniature's
 * download pill already wear.
 */
export const DismissFooterAction = styled(BaseButton)`
	${pillShape};
`;

/** The commit ("Done"). See {@link DismissFooterAction} for why the pair is on the primitive. */
export const DoneFooterAction = styled(BaseButton)`
	${pillShape};
`;

/**
 * The footer login on the locked rung (ui#764), which is that rung's one action and so the primary role.
 */
export const SignInFooterAction = styled(BaseButton)`
	${pillShape};
`;

/**
 * The locked pitch and its way in on ONE line (owner 2026-08-16): plan note and invitation left, the
 * Log in button right, sharing a baseline. The same flex-row-with-gap grammar as `OptionHeader` above,
 * which already pairs a copy block with its control — layout does the spacing.
 */
export const GuestOffer = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	/* The row owns the leading under the toggle now (see GuestOfferCopy), so it reproduces the 8px the
	   plan note used to carry alone. */
	margin-top: 8px;

	/* The breakpoint the sheet's own footer uses: below it FooterAction goes full-width, which would
	   leave the copy nothing to sit in — stack instead, exactly as FooterActions does. */
	@media (max-width: 480px) {
		align-items: stretch;
		flex-direction: column;
	}
`;

/**
 * The copy half of that row. Takes the free space so the button hugs the right edge, and `min-width: 0`
 * lets the sentences wrap rather than forcing the row wider than the sheet.
 */
export const GuestOfferCopy = styled.div`
	flex: 1;
	min-width: 0;

	/* The row supplies the leading, so the first note must not stack its own on top of it — this block
	   opens with the plan note (8px), above the hint (4px). */
	> :first-child {
		margin-top: 0;
	}
`;

/**
 * The same invitation inline with the guest plan note, RIGHT of that copy block (owner 2026-08-12,
 * ui#855; on the same line since owner 2026-08-16), so the pitch reads left-to-right into its call to
 * action. The quieter half of the pair, since the footer carries the primary one.
 */
export const SignInInlineAction = styled(BaseButton)`
	${pillShape};
	flex: none;
`;

/**
 * The five curated faces plus the standard one, as a specimen grid (owner-approved 2026-09-01).
 *
 * `auto-fill` rather than a fixed column count: the builder renders in a CardSheet that is a side band
 * on a desktop and full width on a phone, and a fixed grid would either overflow the narrow case or
 * waste the wide one.
 */
export const FontGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
	gap: 10px;
	margin-top: 8px;
`;

/** One specimen tile. Its selected state reuses the tonal fill the backdrop picker's cards use. */
export const FontCard = styled.label<{$selected: boolean}>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	padding: 10px 8px;
	cursor: pointer;
	border: 1px solid ${({theme, $selected}) => ($selected ? theme.tokens.ink : theme.tokens.line)};
	border-radius: ${radii.panel};
	background: ${({theme, $selected}) => ($selected ? theme.tokens.accentTint : "transparent")};

	&:focus-within {
		outline: 2px solid ${({theme}) => theme.tokens.ink};
		outline-offset: 2px;
	}
`;

/**
 * The specimen itself, drawn IN the face it offers — which is the whole point of a font picker, and why
 * the builder loads all of them (see `recipientFontLoader.loadPersonalizationFontSpecimens`).
 *
 * The family arrives as an inline custom property from the component rather than being named here: the
 * catalogue is the api's, so a stack written into this stylesheet would be a second copy of a list this
 * repo deliberately does not keep — and the font-family sweep would rightly refuse it.
 */
export const FontSpecimen = styled.span`
	font-family: var(--tf-specimen-family);
	font-size: 20px;
	line-height: 1.15;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The Romanian diacritics under every specimen.
 *
 * Not decoration: the owner's hard constraint is that each face carries ă â î ș ț in their comma-below
 * forms, and showing them is how a sender sees their own language render before choosing.
 */
export const FontDiacritics = styled.span`
	font-family: var(--tf-specimen-family);
	font-size: 13px;
	line-height: 1.15;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The face's name, in the APP's own face — so the list stays scannable at a glance. */
export const FontName = styled.span`
	margin-top: 2px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.02em;
	color: ${({theme}) => theme.tokens.ink2};
	text-align: center;
`;

/**
 * The footer row. Once spread commit-left/counterpart-right via auto margins (ui#784); since ui#855
 * (owner) every action clusters on the reading edge, so this is the shared row plus nothing, kept
 * as a named export because the render and its testid address the personalise footer specifically.
 */
export const SpreadFooterActions = styled(FooterActions)``;

/**
 * The logo tile, and the ONE control that opens the picker (ui#1942, owner: "'alege logo' should be
 * removed and the click moved onto the logo actual UI").
 *
 * A step larger than the 96x48 frame it replaces, in the image tiles' own grammar
 * ({@link ../../account/brand/PageThemeSection.styles}): a picture the sender is asked to click has to
 * be big enough to read as a target, and this one is now the whole affordance rather than a preview
 * beside a button.
 */
const LOGO_TILE_WIDTH_PX = 132;
const LOGO_TILE_HEIGHT_PX = 72;

/**
 * What positions the remove control over the tile.
 *
 * A wrapper rather than positioning {@link LogoPickTile} itself, because the ✕ is a `<button>` and the
 * tile is a `<label>`: nested inside it, every click on the ✕ would ALSO open the file picker the label
 * is bound to. Siblings in a positioned box is what keeps the two gestures apart.
 *
 * `width: fit-content` so the box is the tile, not the sheet — otherwise the ✕ would sit at the far
 * right of the section instead of on the corner of the picture it removes.
 */
export const LogoTile = styled.div`
	position: relative;
	width: fit-content;
`;

/**
 * The tile itself: a `<label>` around the visually hidden `<input type="file">`, so the whole picture is
 * the click target and the input keeps its native picker, its keyboard focus and its accessible name.
 * The same arrangement `AddImageLabel` uses for the picture tiles.
 *
 * DASHED while empty and SOLID once a logo is set, which is the one visual difference between "drop
 * something here" and "here is your mark, click to change it" — the two states this single control now
 * has to say apart on its own, with no caption under it.
 */
export const LogoPickTile = styled.label<{$disabled: boolean; $empty: boolean}>`
	width: ${LOGO_TILE_WIDTH_PX}px;
	height: ${LOGO_TILE_HEIGHT_PX}px;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 6px;
	border-radius: ${radii.control};
	border: ${({$empty}) => ($empty ? "1.5px dashed" : "1px solid")}
		${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink3};
	cursor: ${({$disabled}) => ($disabled ? "not-allowed" : "pointer")};
	opacity: ${({$disabled}) => ($disabled ? 0.5 : 1)};

	&:hover {
		border-color: ${({theme, $disabled}) => ($disabled ? theme.tokens.cardBorder : theme.tokens.selectedEdge)};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
`;

/** The empty tile's mark. Decorative — the hidden input carries the name a screen reader announces. */
export const LogoPickGlyph = styled.span`
	font-size: 22px;
	line-height: 1;
`;

/**
 * The shared corner ✕, with its hit area bled out to 44px.
 *
 * {@link TileRemoveButton} draws a 28px disc because the 108px picture tiles it was written for sit in a
 * wrapped row where a larger target would reach into the neighbouring tile. This one has no neighbour —
 * it is alone on a 132px tile — so it takes the `::before` technique
 * (`download/recipient/PresenceIndicator.styles`) that grows the TARGET without growing the disc or
 * covering more of the sender's picture.
 */
export const LogoRemoveButton = styled(TileRemoveButton)`
	&::before {
		content: "";
		position: absolute;
		inset: -8px;
	}
`;

export const DraftError = styled.p`
	margin: 10px 0 0;
	font-size: 12.5px;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/**
 * The builder's two columns: the controls, and the live miniature of the recipient's page (ui#1921).
 *
 * DESKTOP puts the miniature beside the controls in a column of its own, sticky, so it stays on screen
 * while the sender works down the sheet. PHONES have no second column to give it, so the same element
 * becomes a dock at the top of the scroll region — see {@link BuilderPreview}.
 *
 * The miniature comes FIRST in the DOM, which is the phone order and therefore the reading order: the
 * dock and its summary are what the sheet opens with. Desktop places the two columns explicitly rather
 * than relying on source order, so the controls stay on the reading edge there.
 *
 * WHICH COLUMN TAKES THE SLACK FLIPS at the showcase breakpoint, and that is the point of ui#1942. In a
 * 600px sheet there is no slack to give away, so the miniature takes a fixed 252px and the controls take
 * what is left. In the wide sheet the controls are the ones that stop growing — a column of fields and
 * hints has a comfortable measure and gains nothing past it — and every extra pixel goes to the
 * miniature, whose art was squeezed to about 30% of a 252px column.
 */
const CONTROLS_COLUMN = "380px";

export const BuilderLayout = styled.div<{$withPreview: boolean}>`
	display: grid;
	gap: 14px;
	min-width: 0;

	@media (min-width: 641px) {
		grid-template-columns: ${({$withPreview}) => ($withPreview ? "minmax(0, 1fr) 252px" : "minmax(0, 1fr)")};
		align-items: start;
	}

	/* Paired with the wide CardSheet this builder asks for (see PersonaliseSheet's own mount).
	   The same breakpoint, so the sheet and its grid can never widen one without the other. */
	@media (min-width: ${showcase.minWidthPx}px) {
		grid-template-columns: ${({$withPreview}) =>
		($withPreview ? `${CONTROLS_COLUMN} minmax(0, 1fr)` : "minmax(0, 1fr)")};
	}
`;

export const BuilderControls = styled.div`
	min-width: 0;

	@media (min-width: 641px) {
		grid-column: 1;
		grid-row: 1;
	}
`;

/**
 * Where the miniature lives.
 *
 * On desktop, a sticky second column. `top: 0` pins it to the scroll region's CONTENT edge rather than
 * its padding edge, which is deliberate: `CardSheet`'s body dissolves its top band with an alpha mask
 * (`BODY_PADDING_TOP`), and a child pinned INTO that band would be the one element faded out while it
 * is meant to be the one thing still readable.
 *
 * On a phone it is the dock instead — pinned to the same edge, bleeding to the card's own gutters so
 * the controls scroll BEHIND it rather than beside it, and carrying a hairline so the two read as
 * separate surfaces.
 */
export const BuilderPreview = styled.div`
	min-width: 0;

	@media (min-width: 641px) {
		grid-column: 2;
		grid-row: 1;
		position: sticky;
		top: 0;
		align-self: start;
	}

	@media (max-width: 640px) {
		position: sticky;
		top: 0;
		z-index: 2;
		/* The body's phone gutter, cancelled and re-applied, so the dock's background reaches the card
		   edges. Matched to the CardSheet body's own 16px — a wider bleed would show the card through. */
		margin: 0 -16px;
		padding: 4px 16px 10px;
		background: ${({theme}) => theme.tokens.card};
		border-bottom: 1px solid ${({theme}) => theme.tokens.line};
	}
`;
